import React, { useLayoutEffect, useRef, useState } from 'react';
import { FaRegTrashCan } from 'react-icons/fa6';
import { FaDownload } from 'react-icons/fa';
import { Share2 } from 'lucide-react';
import ProgressBar from './ProgressBar';
import Spinner from './Spinner';
import SeriesThumbnail from './SeriesThumbnail.js';
import SeriesHover from './SeriesHover.js';
import SeriesThumbnailPopover from './SeriesThumbnailPopover.js';
import DeleteModal from './DeleteModal';
import StudyTags from './StudyTags';
import ShowMoreButton from './ShowMoreButton';
import { dicomHelpers } from '../services/dicomHelpers';
import './ImagingCheckbox.css';

const SERIES_GRID_MIN_COLUMN_PX = 128;
const SERIES_GRID_GAP_PX = 8;
const COLLAPSED_SERIES_ROWS = 2;

function useGridColumnCount(gridRef) {
    const [columnCount, setColumnCount] = useState(1);

    useLayoutEffect(() => {
        const el = gridRef.current;
        if (!el) return undefined;

        const measure = () => {
            const tracks = window.getComputedStyle(el).gridTemplateColumns;
            const count = tracks && tracks !== 'none' ? tracks.split(' ').length : 1;
            setColumnCount(Math.max(1, count));
        };

        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [gridRef]);

    return columnCount;
}

function ImageCard({
    image,
    onSeriesClick,
    onDelete,
    onDownload,
    onShare,
    onTagsChange,
    confirmTagChanges = false,
    onSeriesHydrationPrefetch,
    hydratedSeriesLookup = {},
    seriesHydrationProgressBySeriesUid = {},
    selectable = false,
    selected = false,
    onToggleSelect,
}) {
    const loading = image.loading;
    const isLoading = !!loading;
    const [savingTags, setSavingTags] = useState(false);

    const handleTagsChange = (nextTags) => {
        if (!onTagsChange || savingTags) return Promise.resolve();
        setSavingTags(true);
        return Promise.resolve(onTagsChange(image, nextTags))
            .finally(() => setSavingTags(false));
    };

    const loadingType = (loading && loading.type) || 'progress';
    const progress = (loading && loading.progress) || image.progress || {};

    const [showDelete, setShowDelete] = useState(false);
    const [deleteAnswer, setDeleteAnswer] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [seriesExpanded, setSeriesExpanded] = useState(false);
    const seriesGridRef = useRef(null);
    const seriesColumnCount = useGridColumnCount(seriesGridRef);

    const seriesList = Array.isArray(image?.series) ? image.series : [];
    const collapsedSeriesCount = seriesColumnCount * COLLAPSED_SERIES_ROWS;
    const hiddenSeriesCount = Math.max(0, seriesList.length - collapsedSeriesCount);
    const visibleSeries = seriesExpanded || hiddenSeriesCount === 0
        ? seriesList
        : seriesList.slice(0, collapsedSeriesCount);

    const closeDelete = () => {
        setShowDelete(false);
    };
    const openDelete = () => {
        setDeleting(false);
        setDeleteAnswer('');
        setShowDelete(true);
    };
    // TODO: REVISIT — no .catch() here, so a failed delete leaves the modal
    // open with no message and raises an unhandled rejection. Part of the
    // delete-failure audit noted on ImagesPage.handleDeleteImage.
    const deleteImage = (e) => {
        e.preventDefault();
        setDeleting(true);
        Promise.resolve(onDelete && onDelete(image))
            .then(() => {
                closeDelete();
            })
            .finally(() => {
                setDeleting(false);
            });
    };

    const downloadImage = () => {
        if (downloading) return;
        setDownloading(true);
        Promise.resolve(onDownload && onDownload(image))
            .finally(() => {
                setDownloading(false);
            });
    };

    const renderLoadingIndicator = () => {
        if (!isLoading) return null;

        if (loadingType === 'spinner') {
            return (
                <div style={{ padding: '0 0.5rem', display: 'flex', justifyContent: 'center' }}>
                    <Spinner label={(loading && loading.label) || 'Loading'} />
                </div>
            );
        }

        return (
            <div style={{ padding: '0 0.5rem' }}>
                <ProgressBar
                    label={progress.label || 'Saving'}
                    percent={progress.percent || 0}
                    detail={progress.detail || ''}
                    visible={true}
                />
            </div>
        );
    };

    return (
        <div className={`review-card${selectable && selected ? ' image-card-selected' : ''}`}>
            <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}
                className="card-line-header"
            >
                <div
                    className="review-header"
                    style={{
                        minHeight: '24px',
                        fontSize: '14px',
                        gap: '10px',
                        alignItems: 'flex-start',
                        flex: '1 1 auto',
                        minWidth: 0
                    }}
                >
                    {selectable && (
                        <input
                            type="checkbox"
                            className="imaging-checkbox"
                            checked={selected}
                            aria-label={`Select ${image.description || 'study'} for sharing`}
                            onChange={() => onToggleSelect?.(image)}
                            style={{ marginTop: '2px' }}
                        />
                    )}
                    <div style={{ color: '#000000', lineHeight: '24px', overflowWrap: 'anywhere' }}>
                        {image.description}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: '24px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {dicomHelpers.displayDate(image.studyDate)}
                </div>
            </div>

            {renderLoadingIndicator()}

            <div
                style={{
                    padding: '0 0.5rem',
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto',
                    transition: 'opacity 200ms ease'
                }}
            >
                <div style={{ padding: '6px 0 0' }}>
                    <StudyTags
                        tags={image.tags || []}
                        editable={!!onTagsChange}
                        requireConfirmation={confirmTagChanges}
                        disabled={savingTags || isLoading}
                        onChange={handleTagsChange}
                    />
                </div>
                <div className="review-details">
                    <div
                        ref={seriesGridRef}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, minmax(${SERIES_GRID_MIN_COLUMN_PX}px, 1fr))`,
                            gap: `${SERIES_GRID_GAP_PX}px`,
                            padding: '8px 0'
                        }}
                    >
                        {visibleSeries.map((s) => {
                            const seriesKey = s.seriesKey || s.seriesInstanceUID;
                            const openSeries = () => {
                                if (onSeriesClick) {
                                    onSeriesClick(image.studyInstanceUID, seriesKey, s.middleIndex);
                                }
                            };
                            const hydrateProgress = seriesHydrationProgressBySeriesUid[seriesKey];
                            const sliceList = Array.isArray(s.slices)
                                ? s.slices
                                : Array.isArray(s.images)
                                    ? s.images
                                    : [];
                            const seriesHydrated =
                                !!hydratedSeriesLookup[seriesKey] || sliceList.length > 0;
                            return (
                                <SeriesThumbnailPopover
                                    key={seriesKey}
                                    onOpen={() =>
                                        onSeriesHydrationPrefetch?.(
                                            image.studyInstanceUID,
                                            seriesKey
                                        )
                                    }
                                    content={
                                        <SeriesHover
                                            series={s}
                                            hydrateProgress={hydrateProgress}
                                            isHydrated={seriesHydrated}
                                            onView={openSeries}
                                        />
                                    }
                                >
                                    <SeriesThumbnail
                                        thumbnailUrl={s.thumbnailUrl}
                                        label={s.label}
                                        sliceCount={s.sliceCount}
                                    />
                                </SeriesThumbnailPopover>
                            );
                        })}
                    </div>
                    {hiddenSeriesCount > 0 && (
                        <div style={{ padding: '0 0 4px' }}>
                            <ShowMoreButton
                                expanded={seriesExpanded}
                                hiddenCount={hiddenSeriesCount}
                                noun="series"
                                onToggle={() => setSeriesExpanded((open) => !open)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {(onDelete || onDownload || onShare) && (
                <div className="card-footer">
                    <div className="card-footer-row">
                        {onShare && (
                            <div
                                className="comment-button"
                                style={{ color: '#2980b9' }}
                                onClick={() => onShare(image)}
                            >
                                <Share2 size={16} strokeWidth={2} /> &nbsp;Share
                            </div>
                        )}
                        {onDownload && (
                            <div
                                className="comment-button"
                                style={{ color: '#2980b9' }}
                                onClick={downloading ? undefined : downloadImage}
                            >
                                <FaDownload /> &nbsp;{downloading ? 'Downloading…' : 'Download'}
                            </div>
                        )}
                        {onDelete && (
                            <div
                                className="comment-button"
                                style={{ color: '#9e1b1b' }}
                                onClick={openDelete}
                            >
                                <FaRegTrashCan /> &nbsp;Delete
                            </div>
                        )}
                    </div>
                </div>
            )}

            {onDelete && (
                <DeleteModal
                    deleteAnswer={deleteAnswer}
                    setDeleteAnswer={setDeleteAnswer}
                    deleteAsset={deleteImage}
                    showDelete={showDelete}
                    deleting={deleting}
                    closeDelete={closeDelete}
                    assetType="image"
                />
            )}
        </div>
    );
}

export default ImageCard;
